import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';
import { CapacitorSafePoller, triggerHapticFeedback } from '../../utils/capacitorHelper';

const YACHANGU_LOGO =
  'https://customer-assets.emergentagent.com/job_yachangu-pay/artifacts/ynseryay_EE6C3FEA-5C5E-424A-B08C-ED1F2A53B790.jpeg';

const glassSheet = {
  backgroundColor: 'rgba(247, 249, 251, 0.82)',
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
};

/**
 * Driver Allocation Modal - Shows progress while finding driver
 * - Progressive bar for 2-minute timeout
 * - Cancel ride option
 * - Cancellation fee warning
 */
const DriverAllocationModal = ({ 
  isOpen, 
  onClose, 
  rideDetails,
  onCancelRide,
  onDriverAccepted
}) => {
  const [progress, setProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [status, setStatus] = useState('searching'); // 'searching', 'found', 'timeout'
  const [acceptancePoller, setAcceptancePoller] = useState(null);
  
  const TIMEOUT_DURATION = 120; // 2 minutes in seconds

  // Expire ride when timeout occurs
  const expireRide = useCallback(async () => {
    if (!rideDetails?.id) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rides/${rideDetails.id}/expire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      console.log('✅ Ride expired due to timeout');
    } catch (error) {
      console.error('Error expiring ride:', error);
    }
  }, [rideDetails?.id]);

  // Progress bar and driver search simulation
  useEffect(() => {
    if (!isOpen || status !== 'searching') return;

    const interval = setInterval(() => {
      setElapsedTime(prev => {
        const newTime = prev + 1;
        const newProgress = (newTime / TIMEOUT_DURATION) * 100;
        setProgress(newProgress);

        // Timeout after 2 minutes
        if (newTime >= TIMEOUT_DURATION) {
          setStatus('timeout');
          clearInterval(interval);
          
          // Expire the ride in the backend
          expireRide();
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, status, expireRide]);

  // Poll for driver acceptance (every 5 seconds) with Capacitor-safe polling
  useEffect(() => {
    if (!isOpen || !rideDetails?.id || status !== 'searching') return;

    const pollDriverAcceptance = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rides/${rideDetails.id}`);
        const data = await response.json();
        
        if (data.status === 'accepted' && data.driver_id) {
          setStatus('found');
          if (poller) {
            poller.stop();
          }
          
          // Haptic feedback for native apps
          triggerHapticFeedback('medium');
          
          // Notify parent that driver was found
          setTimeout(() => {
            onDriverAccepted(data);
          }, 1500);
        }
      } catch (error) {
        console.error('Error polling ride status:', error);
      }
    };

    // Create Capacitor-safe poller
    const poller = new CapacitorSafePoller(pollDriverAcceptance, 5000);
    poller.start();
    setAcceptancePoller(poller);

    return () => {
      if (poller) {
        poller.stop();
      }
    };
  }, [isOpen, rideDetails?.id, status, onDriverAccepted]);

  const handleCancelClick = () => {
    triggerHapticFeedback('light');
    setShowCancelWarning(true);
  };

  const handleConfirmCancel = () => {
    triggerHapticFeedback('medium');
    onCancelRide();
    setShowCancelWarning(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="driver-allocation-modal">
      {/* Frosted emerald backdrop over the live map */}
      <div className="absolute inset-0 bg-emerald-950/25 backdrop-blur-md" />

      {/* Centered pulse search animation */}
      {status === 'searching' && (
        <div className="absolute top-[32%] left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none">
          <span className="absolute w-44 h-44 rounded-full bg-emerald-400/30 animate-ping" />
          <span
            className="absolute w-32 h-32 rounded-full bg-emerald-400/40 animate-ping"
            style={{ animationDelay: '0.7s' }}
          />
          <div className="relative w-20 h-20 rounded-full bg-emerald-600 border-4 border-white shadow-2xl flex items-center justify-center overflow-hidden">
            <img src={YACHANGU_LOGO} alt="Yachangu" className="w-12 h-12 rounded-full object-cover" />
          </div>
        </div>
      )}

      {/* Glass bottom sheet */}
      <section
        className="absolute bottom-6 left-4 right-4 rounded-[32px] border border-white/40 p-6 shadow-[0_20px_40px_rgba(16,185,129,0.12)]"
        style={glassSheet}
      >
        <div className="w-12 h-1.5 bg-gray-300/60 rounded-full mx-auto mb-6" />

        {status === 'searching' && (
          <div className="flex flex-col gap-6">
            {/* Status header */}
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <h2 className="text-2xl font-bold text-gray-900" data-testid="allocation-title">
                  Finding your driver…
                </h2>
                <p className="text-sm text-gray-500">
                  Searching for nearby drivers · {Math.floor(elapsedTime / 60)}:
                  {(elapsedTime % 60).toString().padStart(2, '0')}
                </p>
              </div>
              <div className="w-11 h-11 rounded-full border-4 border-emerald-500/20 border-t-emerald-600 animate-spin" />
            </div>

            {/* Progress bar */}
            <div>
              <div className="w-full bg-gray-200/70 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-emerald-600 h-full transition-all duration-1000 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                {Math.round(progress)}% · Matching you with the best driver
              </p>
            </div>

            {/* Shimmer skeleton driver row */}
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/50">
              <div className="w-16 h-12 rounded-xl bg-gray-200 animate-pulse" />
              <div className="flex flex-col gap-2 flex-grow">
                <div className="h-4 w-1/2 rounded bg-gray-200 animate-pulse" />
                <div className="h-3 w-1/3 rounded bg-gray-200 animate-pulse" />
              </div>
              <div className="w-10 h-4 rounded bg-gray-200 animate-pulse" />
            </div>

            {/* Ride summary */}
            <div className="rounded-2xl bg-white/55 p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Vehicle</span>
                <span className="font-semibold text-gray-800 capitalize">{rideDetails?.vehicle_type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Estimated fare</span>
                <span className="font-semibold text-gray-800">
                  MWK {rideDetails?.estimated_price?.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Distance</span>
                <span className="font-semibold text-gray-800">{rideDetails?.distance != null ? `${Number(rideDetails.distance).toFixed(1)} km` : '—'}</span>
              </div>
            </div>

            {/* Cancel */}
            {!showCancelWarning ? (
              <button
                onClick={handleCancelClick}
                data-testid="allocation-cancel-btn"
                className="w-full h-14 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold text-lg flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                <X className="w-5 h-5" />
                Cancel Search
              </button>
            ) : (
              <div className="border border-red-200 bg-red-50/80 rounded-2xl p-4">
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900 mb-1">Cancel ride?</p>
                    <p className="text-xs text-red-700">
                      You can cancel now without charges since no driver has been assigned yet.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCancelWarning(false)}
                    className="flex-1 h-11 rounded-full border border-gray-300 text-gray-700 text-sm font-medium active:scale-95 transition-transform"
                  >
                    Keep searching
                  </button>
                  <button
                    onClick={handleConfirmCancel}
                    data-testid="allocation-confirm-cancel-btn"
                    className="flex-1 h-11 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold active:scale-95 transition-transform"
                  >
                    Yes, cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {status === 'found' && (
          <div className="text-center py-6" data-testid="allocation-found">
            <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Driver found!</h2>
            <p className="text-gray-500">Loading your driver's details…</p>
          </div>
        )}

        {status === 'timeout' && (
          <div data-testid="allocation-timeout">
            <div className="text-center mb-6">
              <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No drivers available</h2>
              <p className="text-sm text-gray-500">
                We couldn&apos;t find any nearby drivers right now. Please try again in a moment.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onCancelRide}
                className="flex-1 h-14 rounded-full border border-red-400 text-red-600 font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={onClose}
                className="flex-1 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold active:scale-95 transition-transform"
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default DriverAllocationModal;
